import apiService from './apiService';

class FinancialMetricsService {
  constructor() {
    this.baseEndpoint = '/financial-service/api/v1/financial/metrics';
  }

  /**
   * Get portfolio-wide summary metrics
   * @returns {Promise<Object>} Portfolio summary data
   */
  async getPortfolioSummary() {
    try {
      const endpoint = `${this.baseEndpoint}/portfolio-summary`;
      const data = await apiService.get(endpoint);
      return data;
    } catch (error) {
      console.error('Error fetching portfolio summary:', error);
      throw new Error(`Failed to fetch portfolio summary: ${error.message}`);
    }
  }

  /**
   * Get per-deal financial breakdown with pagination
   * @param {Object} paginationParams - Pagination parameters
   * @param {number} paginationParams.page - Page number (0-based)
   * @param {number} paginationParams.size - Page size
   * @param {string} paginationParams.sort - Sort property
   * @param {string} paginationParams.direction - Sort direction (ASC/DESC)
   * @returns {Promise<Object>} Paginated deal breakdown data
   */
  async getDealBreakdown(paginationParams = {}) {
    try {
      const { page = 0, size = 20, sort = 'publicDealId', direction = 'ASC' } = paginationParams;
      const endpoint = `${this.baseEndpoint}/deal-breakdown`;
      
      // Asegurar que page sea un número
      const pageNumber = typeof page === 'number' ? page : parseInt(page, 10) || 0;
      
      const params = new URLSearchParams({
        page: pageNumber.toString(),
        size: size.toString(),
        sort: `${sort},${direction}`
      });
      
      const fullUrl = `${endpoint}?${params}`;
      console.log('FinancialMetricsService - getDealBreakdown URL:', fullUrl);
      console.log('FinancialMetricsService - paginationParams:', paginationParams);
      
      const data = await apiService.get(fullUrl);
      return data;
    } catch (error) {
      console.error('Error fetching deal breakdown:', error);
      throw new Error(`Failed to fetch deal breakdown: ${error.message}`);
    }
  }

  /**
   * Get overdue deals with pagination
   * @param {Object} paginationParams - Pagination parameters
   * @param {number} paginationParams.page - Page number (0-based)
   * @param {number} paginationParams.size - Page size
   * @param {string} paginationParams.sort - Sort property
   * @param {string} paginationParams.direction - Sort direction (ASC/DESC)
   * @returns {Promise<Object>} Paginated overdue deals data
   */
  async getOverdueDeals(paginationParams = {}) {
    try {
      const { page = 0, size = 10, sort = 'overdue', direction = 'DESC' } = paginationParams;
      const endpoint = `${this.baseEndpoint}/overdue-deals`;
      
      // Asegurar que page sea un número
      const pageNumber = typeof page === 'number' ? page : parseInt(page, 10) || 0;
      
      const params = new URLSearchParams({
        page: pageNumber.toString(),
        size: size.toString(),
        sort: `${sort},${direction}`
      });
      
      const fullUrl = `${endpoint}?${params}`;
      console.log('FinancialMetricsService - getOverdueDeals URL:', fullUrl);
      console.log('FinancialMetricsService - paginationParams:', paginationParams);
      
      const data = await apiService.get(fullUrl);
      return data;
    } catch (error) {
      console.error('Error fetching overdue deals:', error);
      throw new Error(`Failed to fetch overdue deals: ${error.message}`);
    }
  }

  /**
   * Get all financial metrics (portfolio summary + deal breakdown)
   * @param {Object} paginationParams - Pagination parameters for deal breakdown
   * @returns {Promise<Object>} Combined metrics data
   */
  async getAllMetrics(paginationParams = {}) {
    try {
      const [portfolioSummary, dealBreakdown] = await Promise.all([
        this.getPortfolioSummary(),
        this.getDealBreakdown(paginationParams)
      ]);

      return {
        portfolioSummary,
        dealBreakdown,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching all metrics:', error);
      throw new Error(`Failed to fetch reports: ${error.message}`);
    }
  }
}

// Create singleton instance
const financialMetricsService = new FinancialMetricsService();

export default financialMetricsService;
